import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Tabs, Form, Input, Switch, InputNumber, Button, Table, Space, message, Popconfirm, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { taxonomyAPI } from '../../Utils/taxonomyApi';
import { languagesAPI } from '../../Utils/api';
import { useLanguage } from '../../Context/LanguageContext';

interface Category {
  id: string;
  slug: string;
  name: string;
  displayOrder: number;
  isActive: boolean;
  isMultiSelect: boolean;
  isFilterable: boolean;
}

interface Option {
  id: string;
  categoryId: string;
  slug: string;
  value: string;
  displayOrder: number;
  isActive: boolean;
}

const CategoryDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [categoryForm] = Form.useForm();

  const [category, setCategory] = useState<Category | null>(null);
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(false);

  // Read active tab from URL query parameter, default to 'general'
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'general');

  // Language Context
  const { currentLanguage, multiLanguageData, setMultiLanguageData } = useLanguage();

  // Update active tab when URL parameter changes
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  useEffect(() => {
    if (id) {
      fetchCategory();
      fetchOptions();
      fetchTranslations();
    }
  }, [id]);

  // Load translations when language changes
  useEffect(() => {
    if (id && category) {
      loadFormDataForLanguage();
    }
  }, [currentLanguage, category]);

  const fetchTranslations = async () => {
    if (!id) return;

    try {
      const response = await languagesAPI.getTranslations('taxonomyCategory', id);
      const translationsData = response.data.translations || {};
      setMultiLanguageData(translationsData);
    } catch (error) {
      console.error('Error fetching translations:', error);
    }
  };

  const loadFormDataForLanguage = () => {
    if (currentLanguage === 'de') {
      // Load German (master) data
      categoryForm.setFieldsValue({
        name: category?.name || '',
        slug: category?.slug || '',
        displayOrder: category?.displayOrder || 0,
        isActive: category?.isActive || false,
        isMultiSelect: category?.isMultiSelect || false,
      });
    } else {
      // Load translation data if exists, keep German as placeholder
      const langData = multiLanguageData[currentLanguage] || {};
      categoryForm.setFieldsValue({
        name: langData.name || '',
        slug: category?.slug || '',
        displayOrder: category?.displayOrder || 0,
        isActive: category?.isActive || false,
        isMultiSelect: category?.isMultiSelect || false,
      });
    }
  };

  // Track form changes and store in multiLanguageData
  const handleFormChange = (changedValues: any, allValues: any) => {
    if (currentLanguage !== 'de') {
      // Store non-German translations
      const translationFields = ['name'];
      const updatedLangData = { ...multiLanguageData[currentLanguage] };

      translationFields.forEach(field => {
        if (changedValues[field] !== undefined) {
          updatedLangData[field] = changedValues[field];
        }
      });

      setMultiLanguageData({
        ...multiLanguageData,
        [currentLanguage]: updatedLangData,
      });
    }
  };

  const fetchCategory = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await taxonomyAPI.getCategory(id);
      const cat = response.data.category || response.data;
      setCategory(cat);
      categoryForm.setFieldsValue(cat);
    } catch (error) {
      message.error('Fehler beim Laden der Kategorie');
    } finally {
      setLoading(false);
    }
  };

  const fetchOptions = async () => {
    if (!id) return;
    setOptionsLoading(true);
    try {
      const response = await taxonomyAPI.getCategoryOptions(id);
      setOptions(response.data.options || []);
    } catch (error) {
      message.error('Fehler beim Laden der Optionen');
    } finally {
      setOptionsLoading(false);
    }
  };

  const handleSaveCategory = async (values: any) => {
    if (!id) return;
    setLoading(true);
    try {
      // CRITICAL: Separate German base data from translations
      const payload: any = {};

      // If user is currently editing in non-German language
      let translationsToSave = { ...multiLanguageData };

      if (currentLanguage !== 'de') {
        // User is editing in non-German mode
        // Store current form edits as translation for current language
        const currentLangData: any = {};
        if (values.name && values.name.trim()) {
          currentLangData.name = values.name;
        }

        if (Object.keys(currentLangData).length > 0) {
          translationsToSave = {
            ...translationsToSave,
            [currentLanguage]: currentLangData,
          };
        }

        // Use original category data for German base (prevent overwriting German with EN)
        payload.name = category?.name || '';
      } else {
        // User is in German mode - use form values for base data
        payload.name = values.name;
      }

      // Always include these fields from form (language-independent)
      payload.slug = values.slug;
      payload.displayOrder = values.displayOrder;
      payload.isActive = values.isActive;
      payload.isMultiSelect = values.isMultiSelect;
      payload.isFilterable = values.isFilterable;

      // Add translations if we have any non-German data
      const hasTranslations = Object.keys(translationsToSave).some(
        lang => lang !== 'de' && translationsToSave[lang] && Object.keys(translationsToSave[lang]).length > 0
      );

      if (hasTranslations) {
        payload.translations = {};
        Object.keys(translationsToSave).forEach(lang => {
          if (lang !== 'de' && translationsToSave[lang]) {
            const langData = translationsToSave[lang];
            const hasData = Object.values(langData).some(val => val && String(val).trim());
            if (hasData) {
              payload.translations[lang] = langData;
            }
          }
        });
      }

      console.log('Category Payload:', payload); // Debug

      await taxonomyAPI.updateCategory(id, payload);
      message.success('Kategorie erfolgreich gespeichert');

      // Update multiLanguageData state with saved translations
      setMultiLanguageData(translationsToSave);

      fetchCategory();
      fetchTranslations();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Fehler beim Speichern der Kategorie');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOption = () => {
    navigate(`/categories/${id}/options/new`);
  };

  const handleEditOption = (option: Option) => {
    navigate(`/categories/${id}/options/${option.id}`);
  };

  const handleDeleteOption = async (optionId: string) => {
    try {
      await taxonomyAPI.deleteOption(optionId);
      message.success('Option erfolgreich gelöscht');
      fetchOptions();
    } catch (error) {
      message.error('Fehler beim Löschen der Option');
    }
  };

  const optionColumns = [
    {
      title: 'Wert',
      dataIndex: 'value',
      key: 'value',
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
    },
    {
      title: 'Reihenfolge',
      dataIndex: 'displayOrder',
      key: 'displayOrder',
      sorter: (a: Option, b: Option) => a.displayOrder - b.displayOrder,
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Aktiv' : 'Inaktiv'}
        </Tag>
      ),
    },
    {
      title: 'Aktionen',
      key: 'actions',
      render: (_: any, record: Option) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditOption(record)}
          >
            Bearbeiten
          </Button>
          <Popconfirm
            title="Option löschen?"
            description="Sind Sie sicher, dass Sie diese Option löschen möchten?"
            onConfirm={() => handleDeleteOption(record.id)}
            okText="Ja"
            cancelText="Nein"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Löschen
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleTabChange = (key: string) => {
    if (key === 'back') {
      navigate('/categories');
    } else {
      setActiveTab(key);
    }
  };

  const tabItems = [
    {
      key: 'back',
      label: <span><ArrowLeftOutlined /> Zurück zur Übersicht</span>,
      children: null, // No content, just navigation
    },
    {
      key: 'general',
      label: 'Allgemeine Einstellungen',
      children: (
        <Form
          form={categoryForm}
          layout="vertical"
          onFinish={handleSaveCategory}
          onValuesChange={handleFormChange}
          style={{ maxWidth: 600 }}
        >
          <Form.Item
            name="name"
            label={`Name${currentLanguage !== 'de' ? ` (${currentLanguage.toUpperCase()})` : ''}`}
            rules={[{ required: true, message: 'Bitte Namen eingeben' }]}
            tooltip={currentLanguage !== 'de' ? 'Deutsche Übersetzung wird als Placeholder verwendet' : undefined}
          >
            <Input
              placeholder={
                currentLanguage === 'de'
                  ? 'z.B. Gehäusematerial'
                  : category?.name || 'Deutsche Übersetzung'
              }
            />
          </Form.Item>

          <Form.Item
            name="slug"
            label="Slug (optional)"
            tooltip="Wird automatisch aus dem Namen generiert, falls leer gelassen"
          >
            <Input placeholder="z.B. case-material (optional)" />
          </Form.Item>

          <Form.Item
            name="displayOrder"
            label="Anzeigereihenfolge"
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Aktiv"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="isMultiSelect"
            label="Mehrfachauswahl erlauben"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="isFilterable"
            label="Durchsuchbar"
            valuePropName="checked"
            tooltip="In der Storefront-Filter-Sidebar anzeigen"
          >
            <Switch />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
              >
                Speichern
              </Button>
              <Button onClick={() => navigate('/categories')}>
                Abbrechen
              </Button>
            </Space>
          </Form.Item>
        </Form>
      ),
    },
    {
      key: 'options',
      label: 'Optionen',
      children: (
        <div>
          <div style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateOption}
            >
              Neue Option
            </Button>
          </div>

          <Table
            columns={optionColumns}
            dataSource={options}
            rowKey="id"
            loading={optionsLoading}
            pagination={{ pageSize: 20 }}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <h1 style={{ marginBottom: 24 }}>
        {category ? `Kategorie: ${category.name}` : 'Kategorie bearbeiten'}
      </h1>

      <Tabs
        items={tabItems}
        activeKey={activeTab}
        onChange={handleTabChange}
      />
    </div>
  );
};

export default CategoryDetailPage;
